// F-WEB-022 /admin/settings — super_admin only. 3 abas: Sistema | Canais | Reseed Taxonomy.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, RotateCw } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import type {
  AdminChannelDetail,
  ChannelAdapter,
  ChannelKind,
  SettingsResponse,
} from "../../lib/types";
import { PageHeader } from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { formatDate } from "../../lib/utils";

export function AdminSettingsPage(): JSX.Element {
  return (
    <div>
      <PageHeader
        title="Configurações"
        description="Parâmetros do sistema, canais e re-seed de taxonomia WC."
      />
      <Tabs defaultValue="system">
        <TabsList>
          <TabsTrigger value="system">Sistema</TabsTrigger>
          <TabsTrigger value="channels">Canais</TabsTrigger>
          <TabsTrigger value="taxonomy">Reseed Taxonomy</TabsTrigger>
        </TabsList>
        <TabsContent value="system"><SystemSettings /></TabsContent>
        <TabsContent value="channels"><ChannelsAdmin /></TabsContent>
        <TabsContent value="taxonomy"><ReseedTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function SystemSettings(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery<SettingsResponse>({
    queryKey: ["admin-settings"],
    queryFn: async () => (await api.get<SettingsResponse>("/api/admin/settings")).data,
  });

  const [editValues, setEditValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data) {
      // Inicializa edit map com JSON-string das values
      const init: Record<string, string> = {};
      for (const [k, v] of Object.entries(data.values)) {
        init[k] = typeof v === "string" ? JSON.stringify(v) : JSON.stringify(v, null, 2);
      }
      setEditValues(init);
    }
  }, [data]);

  const mut = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      await api.patch("/api/admin/settings", patch);
    },
    onSuccess: () => {
      toast.success("Configurações atualizadas");
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  if (isLoading) return <Skeleton className="h-64 w-full mt-4" />;
  if (isError) return <EmptyState title="Erro" description={apiErrorMessage(error)} />;
  if (!data) return <EmptyState title="Sem settings" />;

  const READ_ONLY = new Set([
    "app_version",
    "internal_api_token_rotated_at",
    "jwt_secret_rotated_at",
    "last_drive_sync_at",
  ]);

  const updateSetting = (key: string): void => {
    const raw = editValues[key] ?? "";
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      toast.error(`Valor inválido em ${key}: não é JSON válido`);
      return;
    }
    mut.mutate({ [key]: parsed });
  };

  return (
    <div className="cards" style={{ marginTop: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(min(26rem, 100%), 1fr))" }}>
      {data.meta.map((m) => {
        const ro = READ_ONLY.has(m.key);
        const raw = editValues[m.key] ?? "";
        // Fidelidade 3: valor escalar (string/numero/bool curto, sem quebra
        // de linha) usa campo de UMA linha; JSON/texto longo usa textarea
        // com altura automática (rows por contagem de linha, piso 2, teto 10).
        const isScalarShort = !raw.includes("\n") && raw.length <= 72;
        const rows = isScalarShort ? undefined : Math.min(10, Math.max(2, raw.split("\n").length));
        return (
          <div key={m.key} className="card stack">
            <div>
              <div className="mono" style={{ fontSize: ".8125rem", wordBreak: "break-all" }}>{m.key}</div>
              <div style={{ fontSize: ".8125rem", fontWeight: 600, color: "var(--color-ink-2)", marginTop: ".25rem", lineHeight: 1.5 }}>
                {m.description ?? "—"}
              </div>
            </div>
            <div className="cluster" style={{ alignItems: "flex-start" }}>
              {isScalarShort ? (
                <Input
                  value={raw}
                  onChange={(e) => setEditValues((s) => ({ ...s, [m.key]: e.target.value }))}
                  readOnly={ro}
                  className="mono grow"
                  style={{ fontSize: ".75rem" }}
                />
              ) : (
                <Textarea
                  value={raw}
                  onChange={(e) => setEditValues((s) => ({ ...s, [m.key]: e.target.value }))}
                  readOnly={ro}
                  rows={rows}
                  className="mono grow"
                  style={{ fontSize: ".75rem" }}
                />
              )}
              {ro ? (
                <Badge variant="outline">read-only</Badge>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => updateSetting(m.key)} loading={mut.isPending}>
                  Salvar
                </Button>
              )}
            </div>
            <div style={{ fontSize: ".6875rem", fontWeight: 700, color: "var(--color-ink-3)" }}>
              Atualizado {formatDate(m.updated_at)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ChannelsAdmin(): JSX.Element {
  const qc = useQueryClient();
  const { data = [], isLoading, isError, error } = useQuery<AdminChannelDetail[]>({
    queryKey: ["admin-channels"],
    queryFn: async () => (await api.get<AdminChannelDetail[]>("/api/admin/channels")).data,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminChannelDetail | null>(null);

  if (isLoading) return <Skeleton className="h-64 w-full mt-4" />;
  if (isError) return <EmptyState title="Erro" description={apiErrorMessage(error)} />;

  return (
    <div style={{ marginTop: "1rem" }}>
      <div className="spread" style={{ justifyContent: "flex-end", marginBottom: ".75rem" }}>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="icon" aria-hidden /> Novo canal
        </Button>
      </div>
      {data.length === 0 ? (
        <EmptyState title="Nenhum canal cadastrado" />
      ) : (
        <div className="cards" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(20rem, 1fr))" }}>
          {data.map((c) => (
            <div key={c.id} className="card stack">
              <div className="spread" style={{ alignItems: "flex-start" }}>
                <div className="grow">
                  <div style={{ fontSize: "1rem", fontWeight: 900 }}>{c.name}</div>
                  <div className="cluster" style={{ marginTop: ".375rem", fontSize: ".6875rem" }}>
                    <span className="mono" style={{ color: "var(--color-ink-3)" }}>{c.slug}</span>
                    <Badge variant="outline">{c.kind}</Badge>
                    <Badge variant="outline">{c.adapter}</Badge>
                    {c.active ? <Badge variant="success">ativo</Badge> : <Badge variant="warning">inativo</Badge>}
                  </div>
                </div>
                <Button variant="secondary" size="sm" onClick={() => setEditing(c)}>
                  Editar
                </Button>
              </div>
              <div>
                <Label style={{ fontSize: ".75rem" }}>Config (secrets mascarados)</Label>
                <pre
                  className="mono"
                  style={{
                    fontSize: ".6875rem",
                    background: "var(--color-soft)",
                    border: ".0625rem solid var(--color-line)",
                    borderRadius: "var(--radius-md)",
                    padding: ".5rem",
                    marginTop: ".375rem",
                    maxHeight: "10rem",
                    overflow: "auto",
                  }}
                >
                  {JSON.stringify(c.config, null, 2)}
                </pre>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateChannelDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={() => qc.invalidateQueries({ queryKey: ["admin-channels"] })}
      />
      {editing ? (
        <EditChannelDialog
          channel={editing}
          onOpenChange={(o) => { if (!o) setEditing(null); }}
          onSaved={() => qc.invalidateQueries({ queryKey: ["admin-channels"] })}
        />
      ) : null}
    </div>
  );
}

function CreateChannelDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}): JSX.Element {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ChannelKind>("atacado");
  const [adapter, setAdapter] = useState<ChannelAdapter>("wc");
  const [configJson, setConfigJson] = useState('{\n  "wc_url": "",\n  "consumer_key": "",\n  "consumer_secret": ""\n}');

  const mut = useMutation({
    mutationFn: async () => {
      let config: Record<string, unknown> = {};
      try {
        config = JSON.parse(configJson) as Record<string, unknown>;
      } catch {
        throw new Error("Config JSON inválido");
      }
      await api.post("/api/channels", { slug, name, kind, adapter, config });
    },
    onSuccess: () => {
      toast.success("Canal criado");
      onSaved();
      onOpenChange(false);
      setSlug("");
      setName("");
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo canal</DialogTitle>
          <DialogDescription>
            Define a integração externa (WC, ML, Shopee...). Secrets ficam em config JSONB e podem ser rotacionados via Editar.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}
          className="stack"
          style={{ gap: "var(--sp-3)" }}
        >
          <div>
            <Label htmlFor="ch-slug">Slug</Label>
            <Input id="ch-slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="varejo" pattern="[a-z0-9-]+" required />
          </div>
          <div>
            <Label htmlFor="ch-name">Nome</Label>
            <Input id="ch-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="ch-kind">Kind</Label>
              <Select id="ch-kind" value={kind} onChange={(e) => setKind(e.target.value as ChannelKind)}>
                <option value="atacado">atacado</option>
                <option value="varejo">varejo</option>
                <option value="marketplace">marketplace</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="ch-adapter">Adapter</Label>
              <Select id="ch-adapter" value={adapter} onChange={(e) => setAdapter(e.target.value as ChannelAdapter)}>
                <option value="wc">wc</option>
                <option value="ml">ml</option>
                <option value="shopee">shopee</option>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="ch-config">Config (JSON)</Label>
            <Textarea
              id="ch-config"
              value={configJson}
              onChange={(e) => setConfigJson(e.target.value)}
              rows={6}
              className="mono"
              style={{ fontSize: "var(--text-sm)" }}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" type="button">Cancelar</Button></DialogClose>
            <Button type="submit" loading={mut.isPending}>Criar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditChannelDialog({
  channel,
  onOpenChange,
  onSaved,
}: {
  channel: AdminChannelDetail;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}): JSX.Element {
  const [name, setName] = useState(channel.name);
  const [active, setActive] = useState(channel.active);
  // Config: edit overlay — usuario digita SO chaves que quer mudar (merge no backend).
  const [configOverlay, setConfigOverlay] = useState("{}");

  const mut = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      if (name !== channel.name) body.name = name;
      if (active !== channel.active) body.active = active;
      const trimmed = configOverlay.trim();
      if (trimmed && trimmed !== "{}") {
        try {
          body.config = JSON.parse(trimmed) as Record<string, unknown>;
        } catch {
          throw new Error("Config overlay JSON inválido");
        }
      }
      if (Object.keys(body).length === 0) {
        throw new Error("Nada a salvar");
      }
      await api.patch(`/api/channels/${channel.slug}`, body);
    },
    onSuccess: () => {
      toast.success("Canal atualizado");
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar {channel.slug}</DialogTitle>
          <DialogDescription>
            Config merge: chaves que você mandar substituem, chaves omitidas preservam valor atual (incluindo secrets mascarados).
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}
          className="stack"
          style={{ gap: "var(--sp-3)" }}
        >
          <div>
            <Label htmlFor="ech-name">Nome</Label>
            <Input id="ech-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <Switch
            checked={active}
            onChange={(e) => setActive(e.currentTarget.checked)}
            label="Ativo"
          />
          <div>
            <Label htmlFor="ech-cfg">Config overlay (só chaves pra atualizar)</Label>
            <Textarea
              id="ech-cfg"
              value={configOverlay}
              onChange={(e) => setConfigOverlay(e.target.value)}
              rows={6}
              placeholder={`{\n  "consumer_secret": "novo-segredo"\n}`}
              className="mono"
              style={{ fontSize: "var(--text-sm)" }}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" type="button">Cancelar</Button></DialogClose>
            <Button type="submit" loading={mut.isPending}>Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReseedTab(): JSX.Element {
  const { data: channels = [], isLoading } = useQuery<AdminChannelDetail[]>({
    queryKey: ["admin-channels"],
    queryFn: async () => (await api.get<AdminChannelDetail[]>("/api/admin/channels")).data,
  });
  const wcChannels = useMemo(
    () => channels.filter((c) => c.adapter === "wc" && c.active),
    [channels],
  );

  const mut = useMutation({
    mutationFn: async (slug: string) => {
      const r = await api.post<{ job_id: string }>("/api/admin/reseed-wc-taxonomy", { channel_slug: slug });
      return r.data.job_id;
    },
    onSuccess: (jobId, slug) => {
      toast.success(`Reseed enfileirado para ${slug} (job #${jobId}). Acompanhe em "Log".`);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  if (isLoading) return <Skeleton className="h-32 w-full mt-4" />;

  return (
    <div className="card stack" style={{ marginTop: "1rem" }}>
      <p style={{ fontSize: ".8125rem", fontWeight: 600, color: "var(--color-ink-2)", lineHeight: 1.5, margin: 0 }}>
        Sincroniza categorias canônicas do app pro WooCommerce do canal (RF30 / API-058). Cria categorias
        que faltam, atualiza nomes que mudaram, persiste mapping local-&gt;WC em{" "}
        <code className="mono" style={{ background: "var(--color-soft)", padding: "0 .375rem", borderRadius: "var(--radius-sm)" }}>
          channels.config.wc_category_map
        </code>
        .
      </p>
      {wcChannels.length === 0 ? (
        <EmptyState title="Nenhum canal WC ativo" />
      ) : (
        <div className="stack">
          {wcChannels.map((c) => (
            <div
              key={c.id}
              className="cluster"
              style={{
                justifyContent: "space-between",
                border: ".0625rem solid var(--color-line)",
                borderRadius: "var(--radius-md)",
                padding: ".75rem",
                flexWrap: "wrap",
              }}
            >
              <div className="grow" style={{ minWidth: 0 }}>
                <div style={{ fontSize: ".9375rem", fontWeight: 900 }}>{c.name}</div>
                <div className="mono" style={{ fontSize: ".6875rem", color: "var(--color-ink-3)" }}>{c.slug}</div>
              </div>
              <Button onClick={() => mut.mutate(c.slug)} loading={mut.isPending}>
                <RotateCw className="icon" aria-hidden />
                Reseed agora
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
